import type { CSSProperties } from 'react';
import { alpha, createTheme } from '@mui/material/styles';
import productTheme, { DESIGN_TOKENS as T, DARK_PALETTE } from './theme';
import {
  marketingColors as originalColors,
  marketingFonts as originalFonts,
  marketingShadow as originalShadow,
} from './theme_landing_page';

export { marketingAssets } from './theme_landing_page';

export const marketingDarkPalette = DARK_PALETTE;

export const marketingDarkStyles = {
  '--website-section': marketingDarkPalette.surface,
  '--website-midnight': marketingDarkPalette.surface,
  '--website-glassDark': marketingDarkPalette.elevated,
  '--website-onDark': marketingDarkPalette.text,
  '--website-onDarkSoft': marketingDarkPalette.muted,
  '--website-onDarkMuted': marketingDarkPalette.muted,
  '--website-brandBlue': marketingDarkPalette.accent,
  '--website-brandCyan': marketingDarkPalette.blue,
  '--website-brandViolet': marketingDarkPalette.blueSoft,
  '--website-brandTeal': marketingDarkPalette.teal,
  '--website-primary': marketingDarkPalette.accent,
  '--website-buttonText': marketingDarkPalette.surface,
  '& [data-marketing-surface="light"]': {
    '--website-brandBlue': marketingDarkPalette.accentOnLight,
    '--website-brandViolet': marketingDarkPalette.blueOnLight,
    '--website-brandTeal': marketingDarkPalette.accentOnLight,
    '--website-brandCyan': marketingDarkPalette.blueOnLight,
    '--website-tealText': marketingDarkPalette.accentOnLight,
  },
};

// CSS variables scope the preview to the homepage. Fallbacks preserve shared
// components' existing appearance on every other marketing route.
const colorDefinitions = {
  brandBlue: [originalColors.millosBlue, T.primary],
  brandCyan: [originalColors.millosCyan, T.accentTeal],
  brandViolet: [originalColors.millosViolet, T.primaryActive],
  brandTeal: [originalColors.millosTeal, T.accentTealDark],
  accent: [originalColors.accent, T.primary],
  ink: [originalColors.navy, T.ink],
  body: ['#33415c', T.body],
  muted: ['#667084', T.muted],
  secondary: ['#5a6377', T.muted],
  bodySoft: ['#4c5a72', T.body],
  success: ['#157f45', T.success],
  tealStrong: ['#0f8f78', T.accentTeal],
  tealText: ['#0b6f5d', T.primaryActive],
  mintLight: ['#5fe6cc', T.accentTeal],
  violetLight: ['#a3b4ff', T.onDark],
  cyanLight: ['#7fd6f5', T.onDark],
  blueLight: ['#8fb0ff', T.onDark],
  onDarkMuted: ['#8fa3c7', T.onDarkSoft],
  onDarkSoft: ['#c9d6ee', T.onDark],
  paper: [originalColors.white, T.canvas],
  onDark: [originalColors.white, T.onDark],
  black: ['#000', T.ink],
  shadow: ['#040a1e', T.ink],
  glassDark: ['#0a142a', T.surfaceDarkElevated],
  midnight: ['#0b1530', T.surfaceDark],
  border: [originalColors.cardHairline, T.hairline],
  borderStrong: ['#d3dceb', T.hairline],
  borderSoft: ['#eef1f6', T.hairlineSoft],
  borderBlue: ['#dfe7f5', T.hairline],
  fold: ['#e4eafe', T.surfaceCreamStrong],
  mist: [originalColors.mist, T.surfaceSoft],
  metalLight: ['#d8dde5', T.surfaceCreamStrong],
  metalMid: ['#8f97a3', T.mutedSoft],
  metalHighlight: ['#e4e8ee', T.surfaceCard],
  metalDark: ['#7f8792', T.muted],
  legacyInk: [originalColors.ink, T.ink],
  hero: [originalColors.heroGradient, T.canvas],
  section: [originalColors.heroGradient, marketingDarkPalette.surface],
  buttonText: [originalColors.navy, T.onPrimary],
  buttonHover: [originalColors.millosBlue, T.primaryActive],
} satisfies Record<string, readonly [string, string]>;

const variable = (name: string, fallback: string) => `var(--website-${name}, ${fallback})`;
const color = (name: keyof typeof colorDefinitions) => variable(name, colorDefinitions[name][0]);

export const withOpacity = (value: string, opacity: number): string =>
  `color-mix(in srgb, ${value} ${opacity * 100}%, transparent)`;

export const marketingColors = {
  accent: color('accent'),
  avatarBg: variable('avatarBg', originalColors.avatarBg),
  avatarText: variable('avatarText', originalColors.avatarText),
  body: variable('marketingBody', originalColors.body),
  glassBorder: variable('glassBorder', originalColors.glassBorder),
  hairline: variable('hairline', originalColors.hairline),
  heroGradient: color('section'),
  ink: variable('marketingInk', originalColors.ink),
  millosBlue: color('brandBlue'),
  millosMint: variable('primary', originalColors.millosMint),
  mist: color('mist'),
  muted: variable('marketingMuted', originalColors.muted),
  navy: color('ink'),
  panel: variable('panel', originalColors.panel),
  tintBlue: variable('tintBlue', originalColors.tintBlue),
  tintDivider: variable('tintDivider', originalColors.tintDivider),
  white: variable('white', originalColors.white),
};

export const marketingFonts = {
  display: variable('font-display', originalFonts.display),
  ui: variable('font-ui', originalFonts.ui),
  body: variable('font-body', originalFonts.body),
  wordmark: variable('font-wordmark', originalFonts.wordmark),
  statLabel: variable('font-statLabel', originalFonts.statLabel),
  mono: variable('font-mono', originalFonts.mono),
};

export const marketingShadow = {
  nav: variable('shadow-nav', originalShadow.nav),
  dropdown: variable('shadow-dropdown', originalShadow.dropdown),
};

const colorTokens = {
  blackAlpha8: withOpacity(color('black'), 0.08),
  paperAlpha75: withOpacity(color('onDark'), 0.75),
  paperAlpha18: withOpacity(color('onDark'), 0.18),
  paperAlpha34: withOpacity(color('onDark'), 0.34),
  paperAlpha85: withOpacity(color('onDark'), 0.85),
  brandBlue: color('brandBlue'),
  paperAlpha14: withOpacity(color('onDark'), 0.14),
  paperAlpha40: withOpacity(color('onDark'), 0.4),
  success: color('success'),
  body: color('body'),
  muted: color('muted'),
  ink: color('ink'),
  brandCyan: color('brandCyan'),
  legacyInkAlpha12: withOpacity(color('legacyInk'), 0.12),
  brandViolet: color('brandViolet'),
  brandTeal: color('brandTeal'),
  black: color('black'),
  paperAlpha16: withOpacity(color('onDark'), 0.16),
  paperAlpha28: withOpacity(color('onDark'), 0.28),
  paperAlpha50: withOpacity(color('onDark'), 0.5),
  paperAlpha4: withOpacity(color('onDark'), 0.04),
  paperAlpha8: withOpacity(color('onDark'), 0.08),
  brandVioletAlpha60: withOpacity(color('brandViolet'), 0.6),
  brandVioletAlpha35: withOpacity(color('brandViolet'), 0.35),
  glassDarkAlpha90: withOpacity(color('glassDark'), 0.9),
  brandTealAlpha65: withOpacity(color('brandTeal'), 0.65),
  brandCyanAlpha18: withOpacity(color('brandCyan'), 0.18),
  brandCyanAlpha60: withOpacity(color('brandCyan'), 0.6),
  paper: color('paper'),
  onDarkMuted: color('onDarkMuted'),
  violetLight: color('violetLight'),
  fold: color('fold'),
  mintLight: color('mintLight'),
  tealStrong: color('tealStrong'),
  tealText: color('tealText'),
  secondary: color('secondary'),
  border: color('border'),
  brandBlueAlpha22: withOpacity(color('brandBlue'), 0.22),
  brandTealAlpha26: withOpacity(color('brandTeal'), 0.26),
  cyanLight: color('cyanLight'),
  blueLight: color('blueLight'),
  shadowAlpha30: withOpacity(color('shadow'), 0.3),
  glassDarkAlpha82: withOpacity(color('glassDark'), 0.82),
  paperAlpha55: withOpacity(color('onDark'), 0.55),
  brandVioletAlpha55: withOpacity(color('brandViolet'), 0.55),
  onDarkSoft: color('onDarkSoft'),
  bodySoft: color('bodySoft'),
  mistAlpha94: withOpacity(color('mist'), 0.94),
  borderStrong: color('borderStrong'),
  inkAlpha12: withOpacity(color('ink'), 0.12),
  borderSoft: color('borderSoft'),
  borderBlue: color('borderBlue'),
  paperAlpha32: withOpacity(color('onDark'), 0.32),
  paperAlpha90: withOpacity(color('onDark'), 0.9),
  accent: color('accent'),
  metalLight: color('metalLight'),
  metalMid: color('metalMid'),
  metalHighlight: color('metalHighlight'),
  metalDark: color('metalDark'),
  midnight: color('midnight'),
  paperAlpha86: withOpacity(color('onDark'), 0.86),
  paperAlpha60: withOpacity(color('onDark'), 0.6),
  shadowAlpha40: withOpacity(color('shadow'), 0.4),
  shadowAlpha38: withOpacity(color('shadow'), 0.38),
  shadowAlpha35: withOpacity(color('shadow'), 0.35),
  brandVioletAlpha22: withOpacity(color('brandViolet'), 0.22),
  shadowAlpha50: withOpacity(color('shadow'), 0.5),
  shadowAlpha28: withOpacity(color('shadow'), 0.28),
  inkAlpha18: withOpacity(color('ink'), 0.18),
};

const styleDefaults = {
  fontWeight400: "400",
  sectionHeadingSize: "clamp(2rem, 4.4vw, 4rem)",
  lineHeight1_2: "1.2",
  letterSpacing_0_035em: "-0.035em",
  borderRadius32px: "32px",
  fontSize12: "12px",
  fontWeight500: "500",
  letterSpacing0_075rem: "0.075rem",
  fontSize16: "16px",
  lineHeight1_5: "1.5",
  heroHeadingSize: "clamp(2.4255rem,5.544vw,4.62rem)",
  lineHeight1_04: "1.04",
  letterSpacing_0_045em: "-0.045em",
  heroHeadingShadow: `0 2px 24px ${colorTokens.shadowAlpha40}`,
  heroSubtitleSize: "clamp(18.48px,1.848vw,25.41px)",
  lineHeight1_4: "1.4",
  heroSubtitleShadow: `0 2px 16px ${colorTokens.shadowAlpha40}`,
  fontWeight600: "600",
  letterSpacing0_08em: "0.08em",
  borderRadius22px: "22px",
  fontSize15_5: "15.5px",
  borderRadius20px: "20px",
  fontWeight700: "700",
  statSize: "clamp(32px,3.9vw,56px)",
  lineHeight1: "1",
  letterSpacing_0_03em: "-0.03em",
  fontSize14: "14px",
  lineHeight1_45: "1.45",
  borderRadius16px: "16px",
  fontSize26: "26px",
  fontSize24: "24px",
  lineHeight1_1: "1.1",
  fontSize15: "15px",
  borderRadius24px: "24px",
  fontSize11: "11px",
  letterSpacing0_06rem: "0.06rem",
  fontSize20: "20px",
  lineHeight1_35: "1.35",
  borderRadius0_0_24px_24px: "0 0 24px 24px",
  wordmarkSize: "clamp(24px,2.5vw,30px)",
  navSize: "clamp(16px,1.3vw,17px)",
  borderRadius24: "24px",
  navButtonSize: "clamp(15px,1.2vw,16px)",
  borderRadius12px: "12px",
  fontSize22: "22px",
  fontSize13_5: "13.5px",
  borderRadius9px: "9px",
  borderRadius11px: "11px",
  fontSize12_5: "12.5px",
  borderRadius8px: "8px",
  fontSize11_5: "11.5px",
  letterSpacing_0_01em: "-0.01em",
  fontSize13: "13px",
  footerWordmarkSize: "clamp(3.5rem, 15vw, 13rem)",
  lineHeight1_28: "1.28",
  letterSpacing_0_02em: "-0.02em",
  borderRadius32: "32px",
  backdropFilterblur_14px: "blur(14px)",
  borderRadius18px: "18px",
  flowCardShadow: `0 18px 40px ${colorTokens.shadowAlpha38}, inset 0 1px 0 ${colorTokens.paperAlpha8}`,
  certificateShadow: `drop-shadow(0 4px 8px ${colorTokens.shadowAlpha30})`,
  fontSize10: "10px",
  emailShadow: `drop-shadow(0 3px 5px ${colorTokens.shadowAlpha35})`,
  borderRadius2px: "2px",
  borderRadius6px: "6px",
  borderRadius14px: "14px",
  fontSize2_1rem: "2.1rem",
  lineHeight1_12: "1.12",
  lineHeight1_15: "1.15",
  lineHeight1_55: "1.55",
  letterSpacing0_14em: "0.14em",
  letterSpacing_0_04em: "-0.04em",
  fontSize34: "34px",
  agentsHeadingSize: "clamp(2rem,3.8vw,3.4rem)",
  letterSpacing0_1em: "0.1em",
  letterSpacing0: "0",
  letterSpacing0_12em: "0.12em",
  orbitHeadingSize: "clamp(2.2rem,4.6vw,4rem)",
  sectionHeadingShadow: `0 2px 24px ${colorTokens.shadowAlpha35}`,
  fontSize18: "18px",
  sectionCopyShadow: `0 1px 14px ${colorTokens.shadowAlpha40}`,
  orbitHubShadow: `0 0 0 8px ${colorTokens.brandVioletAlpha22}, 0 24px 50px ${colorTokens.shadowAlpha30}`,
  backdropFilterblur_12px: "blur(12px)",
  orbitCardShadow: `0 18px 40px ${colorTokens.shadowAlpha30}`,
  fontSize14_5: "14.5px",
  stackHeadingSize: "clamp(2.2rem,4.4vw,3.8rem)",
  fontSize17: "17px",
  lineHeight1_6: "1.6",
  borderRadius28px: "28px",
  closingHeadingSize: "clamp(2.1rem,4.2vw,3.4rem)",
  lineHeight1_08: "1.08",
  borderRadius56px: "56px",
  phoneShadow: `0 50px 110px ${colorTokens.shadowAlpha50}`,
  borderRadius46px: "46px",
  borderRadius42px: "42px",
  fontSize74: "74px",
  lineHeight0_95: "0.95",
  backdropFilterblur_18px: "blur(18px)",
  notificationShadow: `0 10px 28px ${colorTokens.shadowAlpha28}`,
  notificationIconShadow: `0 1px 3px ${colorTokens.inkAlpha18}`,
  letterSpacing0_06em: "0.06em",
};

const weightVariable = (name: string, fallback: string): CSSProperties['fontWeight'] =>
  variable(name, fallback) as CSSProperties['fontWeight'];

export const visualTokens = {
  ...colorTokens,
  onDark: color('onDark'),
  replayBackground: variable('replayBackground', color('brandTeal')),
  replayHover: variable('replayHover', color('mintLight')),
  buttonText: color('buttonText'),
  buttonHover: color('buttonHover'),
  agentTaglineSize: variable('agentTaglineSize', 'clamp(1.6rem,2.4vw,2.2rem)'),
  agentTaglineCompactSize: variable('agentTaglineCompactSize', 'clamp(1.3rem,2vw,1.6rem)'),
  agentTitleSize: variable('agentTitleSize', 'clamp(2.6rem,4.4vw,3.8rem)'),
  agentTitleCompactSize: variable('agentTitleCompactSize', 'clamp(2.1rem,3.4vw,2.7rem)'),
  wordmarkDefaultSize: variable('wordmarkDefaultSize', '28px'),
  fontWeight400: weightVariable('style-fontWeight400', styleDefaults.fontWeight400),
  sectionHeadingSize: variable('style-sectionHeadingSize', styleDefaults.sectionHeadingSize),
  lineHeight1_2: variable('style-lineHeight1_2', styleDefaults.lineHeight1_2),
  letterSpacing_0_035em: variable('style-letterSpacing_0_035em', styleDefaults.letterSpacing_0_035em),
  borderRadius32px: variable('style-borderRadius32px', styleDefaults.borderRadius32px),
  fontSize12: variable('style-fontSize12', styleDefaults.fontSize12),
  fontWeight500: weightVariable('style-fontWeight500', styleDefaults.fontWeight500),
  letterSpacing0_075rem: variable('style-letterSpacing0_075rem', styleDefaults.letterSpacing0_075rem),
  fontSize16: variable('style-fontSize16', styleDefaults.fontSize16),
  lineHeight1_5: variable('style-lineHeight1_5', styleDefaults.lineHeight1_5),
  heroHeadingSize: variable('style-heroHeadingSize', styleDefaults.heroHeadingSize),
  lineHeight1_04: variable('style-lineHeight1_04', styleDefaults.lineHeight1_04),
  letterSpacing_0_045em: variable('style-letterSpacing_0_045em', styleDefaults.letterSpacing_0_045em),
  heroHeadingShadow: variable('style-heroHeadingShadow', styleDefaults.heroHeadingShadow),
  heroSubtitleSize: variable('style-heroSubtitleSize', styleDefaults.heroSubtitleSize),
  lineHeight1_4: variable('style-lineHeight1_4', styleDefaults.lineHeight1_4),
  heroSubtitleShadow: variable('style-heroSubtitleShadow', styleDefaults.heroSubtitleShadow),
  fontWeight600: weightVariable('style-fontWeight600', styleDefaults.fontWeight600),
  letterSpacing0_08em: variable('style-letterSpacing0_08em', styleDefaults.letterSpacing0_08em),
  borderRadius22px: variable('style-borderRadius22px', styleDefaults.borderRadius22px),
  fontSize15_5: variable('style-fontSize15_5', styleDefaults.fontSize15_5),
  borderRadius20px: variable('style-borderRadius20px', styleDefaults.borderRadius20px),
  fontWeight700: weightVariable('style-fontWeight700', styleDefaults.fontWeight700),
  statSize: variable('style-statSize', styleDefaults.statSize),
  lineHeight1: variable('style-lineHeight1', styleDefaults.lineHeight1),
  letterSpacing_0_03em: variable('style-letterSpacing_0_03em', styleDefaults.letterSpacing_0_03em),
  fontSize14: variable('style-fontSize14', styleDefaults.fontSize14),
  lineHeight1_45: variable('style-lineHeight1_45', styleDefaults.lineHeight1_45),
  borderRadius16px: variable('style-borderRadius16px', styleDefaults.borderRadius16px),
  fontSize26: variable('style-fontSize26', styleDefaults.fontSize26),
  fontSize24: variable('style-fontSize24', styleDefaults.fontSize24),
  lineHeight1_1: variable('style-lineHeight1_1', styleDefaults.lineHeight1_1),
  fontSize15: variable('style-fontSize15', styleDefaults.fontSize15),
  borderRadius24px: variable('style-borderRadius24px', styleDefaults.borderRadius24px),
  fontSize11: variable('style-fontSize11', styleDefaults.fontSize11),
  letterSpacing0_06rem: variable('style-letterSpacing0_06rem', styleDefaults.letterSpacing0_06rem),
  fontSize20: variable('style-fontSize20', styleDefaults.fontSize20),
  lineHeight1_35: variable('style-lineHeight1_35', styleDefaults.lineHeight1_35),
  borderRadius0_0_24px_24px: variable('style-borderRadius0_0_24px_24px', styleDefaults.borderRadius0_0_24px_24px),
  wordmarkSize: variable('style-wordmarkSize', styleDefaults.wordmarkSize),
  navSize: variable('style-navSize', styleDefaults.navSize),
  borderRadius24: variable('style-borderRadius24', styleDefaults.borderRadius24),
  navButtonSize: variable('style-navButtonSize', styleDefaults.navButtonSize),
  borderRadius12px: variable('style-borderRadius12px', styleDefaults.borderRadius12px),
  fontSize22: variable('style-fontSize22', styleDefaults.fontSize22),
  fontSize13_5: variable('style-fontSize13_5', styleDefaults.fontSize13_5),
  borderRadius9px: variable('style-borderRadius9px', styleDefaults.borderRadius9px),
  borderRadius11px: variable('style-borderRadius11px', styleDefaults.borderRadius11px),
  fontSize12_5: variable('style-fontSize12_5', styleDefaults.fontSize12_5),
  borderRadius8px: variable('style-borderRadius8px', styleDefaults.borderRadius8px),
  fontSize11_5: variable('style-fontSize11_5', styleDefaults.fontSize11_5),
  letterSpacing_0_01em: variable('style-letterSpacing_0_01em', styleDefaults.letterSpacing_0_01em),
  fontSize13: variable('style-fontSize13', styleDefaults.fontSize13),
  footerWordmarkSize: variable('style-footerWordmarkSize', styleDefaults.footerWordmarkSize),
  lineHeight1_28: variable('style-lineHeight1_28', styleDefaults.lineHeight1_28),
  letterSpacing_0_02em: variable('style-letterSpacing_0_02em', styleDefaults.letterSpacing_0_02em),
  borderRadius32: variable('style-borderRadius32', styleDefaults.borderRadius32),
  backdropFilterblur_14px: variable('style-backdropFilterblur_14px', styleDefaults.backdropFilterblur_14px),
  borderRadius18px: variable('style-borderRadius18px', styleDefaults.borderRadius18px),
  flowCardShadow: variable('style-flowCardShadow', styleDefaults.flowCardShadow),
  certificateShadow: variable('style-certificateShadow', styleDefaults.certificateShadow),
  fontSize10: variable('style-fontSize10', styleDefaults.fontSize10),
  emailShadow: variable('style-emailShadow', styleDefaults.emailShadow),
  borderRadius2px: variable('style-borderRadius2px', styleDefaults.borderRadius2px),
  borderRadius6px: variable('style-borderRadius6px', styleDefaults.borderRadius6px),
  borderRadius14px: variable('style-borderRadius14px', styleDefaults.borderRadius14px),
  fontSize2_1rem: variable('style-fontSize2_1rem', styleDefaults.fontSize2_1rem),
  lineHeight1_12: variable('style-lineHeight1_12', styleDefaults.lineHeight1_12),
  lineHeight1_15: variable('style-lineHeight1_15', styleDefaults.lineHeight1_15),
  lineHeight1_55: variable('style-lineHeight1_55', styleDefaults.lineHeight1_55),
  letterSpacing0_14em: variable('style-letterSpacing0_14em', styleDefaults.letterSpacing0_14em),
  letterSpacing_0_04em: variable('style-letterSpacing_0_04em', styleDefaults.letterSpacing_0_04em),
  fontSize34: variable('style-fontSize34', styleDefaults.fontSize34),
  agentsHeadingSize: variable('style-agentsHeadingSize', styleDefaults.agentsHeadingSize),
  letterSpacing0_1em: variable('style-letterSpacing0_1em', styleDefaults.letterSpacing0_1em),
  letterSpacing0: variable('style-letterSpacing0', styleDefaults.letterSpacing0),
  letterSpacing0_12em: variable('style-letterSpacing0_12em', styleDefaults.letterSpacing0_12em),
  orbitHeadingSize: variable('style-orbitHeadingSize', styleDefaults.orbitHeadingSize),
  sectionHeadingShadow: variable('style-sectionHeadingShadow', styleDefaults.sectionHeadingShadow),
  fontSize18: variable('style-fontSize18', styleDefaults.fontSize18),
  sectionCopyShadow: variable('style-sectionCopyShadow', styleDefaults.sectionCopyShadow),
  orbitHubShadow: variable('style-orbitHubShadow', styleDefaults.orbitHubShadow),
  backdropFilterblur_12px: variable('style-backdropFilterblur_12px', styleDefaults.backdropFilterblur_12px),
  orbitCardShadow: variable('style-orbitCardShadow', styleDefaults.orbitCardShadow),
  fontSize14_5: variable('style-fontSize14_5', styleDefaults.fontSize14_5),
  stackHeadingSize: variable('style-stackHeadingSize', styleDefaults.stackHeadingSize),
  fontSize17: variable('style-fontSize17', styleDefaults.fontSize17),
  lineHeight1_6: variable('style-lineHeight1_6', styleDefaults.lineHeight1_6),
  borderRadius28px: variable('style-borderRadius28px', styleDefaults.borderRadius28px),
  closingHeadingSize: variable('style-closingHeadingSize', styleDefaults.closingHeadingSize),
  lineHeight1_08: variable('style-lineHeight1_08', styleDefaults.lineHeight1_08),
  borderRadius56px: variable('style-borderRadius56px', styleDefaults.borderRadius56px),
  phoneShadow: variable('style-phoneShadow', styleDefaults.phoneShadow),
  borderRadius46px: variable('style-borderRadius46px', styleDefaults.borderRadius46px),
  borderRadius42px: variable('style-borderRadius42px', styleDefaults.borderRadius42px),
  fontSize74: variable('style-fontSize74', styleDefaults.fontSize74),
  lineHeight0_95: variable('style-lineHeight0_95', styleDefaults.lineHeight0_95),
  backdropFilterblur_18px: variable('style-backdropFilterblur_18px', styleDefaults.backdropFilterblur_18px),
  notificationShadow: variable('style-notificationShadow', styleDefaults.notificationShadow),
  notificationIconShadow: variable('style-notificationIconShadow', styleDefaults.notificationIconShadow),
  letterSpacing0_06em: variable('style-letterSpacing0_06em', styleDefaults.letterSpacing0_06em),
};

const px = (value: string | number | undefined) =>
  typeof value === 'number' ? `${value}px` : value;

const productStyleOverrides: Partial<Record<keyof typeof styleDefaults, string>> = {
  fontWeight400: String(productTheme.typography.displayLg.fontWeight),
  heroHeadingSize: `clamp(${px(productTheme.typography.displayMd.fontSize)}, 5.544vw, ${px(productTheme.typography.displayXl.fontSize)})`,
  heroSubtitleSize: String(px(productTheme.typography.titleLg.fontSize)),
  sectionHeadingSize: `clamp(${px(productTheme.typography.displaySm.fontSize)}, 4.4vw, ${px(productTheme.typography.displayLg.fontSize)})`,
  agentsHeadingSize: String(px(productTheme.typography.displayLg.fontSize)),
  orbitHeadingSize: String(px(productTheme.typography.displayLg.fontSize)),
  stackHeadingSize: String(px(productTheme.typography.displayLg.fontSize)),
  closingHeadingSize: String(px(productTheme.typography.displayLg.fontSize)),
  statSize: String(px(productTheme.typography.displayLg.fontSize)),
  wordmarkSize: `calc(${px(productTheme.typography.titleLg.fontSize)} * 1.1)`,
  navSize: String(px(productTheme.typography.navLink.fontSize)),
  navButtonSize: String(px(productTheme.typography.button.fontSize)),
  fontSize24: String(px(productTheme.typography.titleLg.fontSize)),
  fontSize26: String(px(productTheme.typography.displaySm.fontSize)),
  fontSize34: String(px(productTheme.typography.displayMd.fontSize)),
  fontSize15: String(px(productTheme.typography.bodyMd.fontSize)),
  fontSize15_5: String(px(productTheme.typography.bodyMd.fontSize)),
  fontSize17: String(px(productTheme.typography.bodyMd.fontSize)),
  fontSize14_5: String(px(productTheme.typography.bodySm.fontSize)),
  heroHeadingShadow: 'none',
  heroSubtitleShadow: 'none',
  sectionHeadingShadow: 'none',
  sectionCopyShadow: 'none',
  flowCardShadow: productTheme.shadows[1],
  orbitCardShadow: productTheme.shadows[1],
  notificationShadow: productTheme.shadows[2],
  notificationIconShadow: productTheme.shadows[1],
  backdropFilterblur_12px: 'none',
  backdropFilterblur_14px: 'none',
  backdropFilterblur_18px: 'none',
};

const styleVariables = Object.fromEntries(
  Object.entries(styleDefaults).map(([name, fallback]) => {
    let value = productStyleOverrides[name as keyof typeof styleDefaults] ?? fallback;
    if (name.startsWith('borderRadius') && ![42, 46, 56].includes(parseFloat(fallback))) {
      const productRadius = parseFloat(String(productTheme.shape.borderRadius));
      const radius = Math.min(parseFloat(fallback) || productRadius, productRadius);
      value = fallback.startsWith('0 0') ? `0 0 ${radius}px ${radius}px` : `${radius}px`;
    }
    if (name.startsWith('letterSpacing')) {
      value = parseFloat(fallback) > 0
        ? String(px(productTheme.typography.captionUppercase.letterSpacing))
        : String(px(productTheme.typography.bodyMd.letterSpacing ?? 0));
    }
    return [`--website-style-${name}`, value];
  })
);

export const homepageVariables: Record<`--website-${string}`, string | undefined> = {
  '--website-mill-blade-direction': '-1',
  ...Object.fromEntries(Object.entries(colorDefinitions).map(([name, values]) => [`--website-${name}`, values[1]])),
  ...styleVariables,
  '--website-avatarBg': T.surfaceCard,
  '--website-avatarText': T.bodyStrong,
  '--website-marketingBody': T.body,
  '--website-glassBorder': T.hairline,
  '--website-hairline': T.hairline,
  '--website-marketingInk': T.ink,
  '--website-primary': T.primary,
  '--website-marketingMuted': T.muted,
  '--website-panel': T.surfaceSoft,
  '--website-tintBlue': alpha(T.primary, 0.12),
  '--website-tintDivider': T.hairlineSoft,
  '--website-white': T.canvas,
  '--website-font-display': productTheme.typography.displayLg.fontFamily,
  '--website-font-ui': productTheme.typography.titleMd.fontFamily,
  '--website-font-body': productTheme.typography.bodyMd.fontFamily,
  '--website-font-wordmark': productTheme.typography.titleLg.fontFamily,
  '--website-font-statLabel': productTheme.typography.titleMd.fontFamily,
  '--website-font-mono': productTheme.typography.code.fontFamily,
  '--website-shadow-nav': productTheme.shadows[1],
  '--website-shadow-dropdown': productTheme.shadows[8],
  '--website-agentTaglineSize': px(productTheme.typography.displayMd.fontSize),
  '--website-agentTaglineCompactSize': px(productTheme.typography.displaySm.fontSize),
  '--website-agentTitleSize': px(productTheme.typography.displayLg.fontSize),
  '--website-agentTitleCompactSize': px(productTheme.typography.displayMd.fontSize),
  '--website-wordmarkDefaultSize': px(productTheme.typography.titleLg.fontSize),
  '--website-replayBackground': T.primary,
  '--website-replayHover': T.primaryActive,
};

export const homepageStyles = {
  ...homepageVariables,
  '& h1': {
    fontWeight: productTheme.typography.displayXl.fontWeight,
    lineHeight: productTheme.typography.displayXl.lineHeight,
    letterSpacing: productTheme.typography.displayXl.letterSpacing,
  },
  '& h2': {
    fontWeight: productTheme.typography.displayLg.fontWeight,
    lineHeight: productTheme.typography.displayLg.lineHeight,
    letterSpacing: productTheme.typography.displayLg.letterSpacing,
  },
  '& [data-millos-flow]': {
    '--website-glassDark': T.surfaceCard,
    '--website-onDark': T.ink,
    '--website-violetLight': T.primaryActive,
    '--website-mintLight': T.primaryActive,
    '--website-onDarkMuted': T.muted,
  },
};

// Drawers render in a portal outside the page DOM, so inherit the same variables
// through the scoped MUI theme rather than leaking them onto document.body.
export const homepageTheme = createTheme(productTheme, {
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: homepageVariables,
      },
    },
  },
});

export const homepageHero = {
  background: color('hero'),
  text: marketingColors.ink,
  muted: marketingColors.muted,
  chipBackground: marketingColors.panel,
  chipBorder: marketingColors.hairline,
};

export const homepageMockups = {
  panel: {
    background: marketingColors.white,
    color: marketingColors.ink,
    border: `1px solid ${marketingColors.glassBorder}`,
    borderRadius: visualTokens.borderRadius24px,
    boxShadow: variable('shadow-dropdown', originalShadow.product),
    fontFamily: marketingFonts.body,
    textAlign: 'left',
  },
  success: T.success,
  successText: T.bodyStrong,
  successBackground: alpha(T.success, 0.14),
  chartLine: marketingColors.millosBlue,
  chartFill: alpha(T.primary, 0.1),
  gridLine: marketingColors.hairline,
};
